<template>
  <div style="width: 40rem; max-width: 80vw;">
    <h1 class="h3">{{ $t('view.SignUp.title') }}</h1>
    <ds ds="form"
      id="sign-up"
      @valid="e => isValid = e.detail"
      @submit="submitSignUp"
    >
      <div class="_grid">
        <div class="_col:xs(12) _col(6)">
          <ds ds="input-string"
            name="email"
            :label="$t('shared.label.email') + ' *'"
            type="email"
            :value="email"
            @dsInput="({ detail }) => (email = typeof detail === 'string' ? detail.toLocaleLowerCase() : detail)"
            :validate="ds.vEmail"
            class="_m:bottom(3)"
          />
        </div>
        <div class="_col:xs(12) _col(6)">
          <ds ds="select-country"
            id="country"
            name="country"
            returnType="alpha3"
            :value.sync="country"
            :label="$t('view.SignUp.labels.country')"
            :isRequired="true"
          />
        </div>
        <div class="_col:xs(12) _col(6)">
          <ds ds="input-string"
            name="password"
            :label="$t('shared.label.password') + ' *'"
            type="password"
            :value.sync="password"
            :validate="v => ds.vMinLength(8)(v) || ds.vIncludeUppercase(v)|| ds.vIncludeLowercase(v) || ds.vIncludeNumber(v)"
            class="_m:bottom(3)"
          />
        </div>
        <div class="_col:xs(12) _col(6)">
          <ds ds="input-string"
            name="confirmPassword"
            :label="$t('shared.label.confirmPassword') + ' *'"
            type="password"
            :value.sync="confirmPassword"
            :validate="v => ds.vOneOf([password])(v) ? $t('view.SignUp.passwordMismatch') : undefined"
          />
        </div>
        <div class="_col(12)">
          <div class="investor-type">
            <div class="_text:sm _text(500) _p:x(3) _m:bottom(3)">
              {{$t('view.SignUp.labels.investorType') + ' *'}}
            </div>
            <ds ds="input-radio"
              id="investorType-1"
              name="investorType"
              :value.sync="investorType"
              checkedValue="individual"
              :label="$t('view.SignUp.labels.investorTypePhysical')"
              :validate="ds.vString"
              :isDisabled="this.individualDisabled"
              class="_p:left(1)"
            />
            <ds ds="input-radio"
              id="investorType-2"
              name="investorType"
              :value.sync="investorType"
              checkedValue="institution"
              :label="$t('view.SignUp.labels.investorTypeLegal')"
              :validate="ds.vString"
              :isDisabled="this.institutionDisabled"
              class="_p:left(1)"
            />
            <ds ds="tag"
              version="warning"
              :label="$t('view.SignUp.warnings.investorTypeLegal')"
              :isClearable="true"
              :isSquare="true"
              v-if="investorType === 'institution'"
              class="_m:top(3) _m:bottom(1)"
            />
          </div>
          <ds ds="input-checkbox"
            name="terms"
            :label="$t('view.SignUp.labels.terms')"
            :checkedValue="true"
            :value.sync="terms"
            class="_m:top(4) _p:x(1) _p:y(2)"
            :validate="ds.vTrue"
          >

          <div slot="helperText"
            v-html="`
              ${ $t('view.SignUp.signUpTerms') }
           `"
          />
          </ds>
        </div>
      </div>
      <ds ds="button"
        name="sign-up"
        :label="$t('view.SignUp.action')"
        version="primary"
        :isLoading="isLoading"
        icon="arrow-right"
        type="submit"
        class="_m:top(3) _m:bottom(3)"
      />
      <ds ds="button"
        id="sign-in"
        :label="$t('view.SignUp.labels.iAlreadyHaveAnAccount')"
        mode="text"
        :isFullWidth="true"
        @click="$router.push({ name: 'sign-in' })"
        class="auth-footer"
      />
    </ds>
  </div>
</template>

<script>
import { eventBus } from '@/eventBus';
import { mapActions, mapGetters } from 'vuex';
import { LoggerService } from '@/services';
import { authHelper } from '@/mixins';
import { authGuard } from '@tokenyico/vuejs-toolkit/src/exports/mixins/guards';

export default {
  name: 'SignUp',
  mixins: [authHelper, authGuard({ name: 'appIndex' })],
  data: () => ({
    email: '',
    password: '',
    confirmPassword: '',
    investorType: null,
    country: null,
    terms: false,
    isLoading: false,
    isValid: false

  }),
  computed: {
    ...mapGetters(
      {
        languages: 'qualification/languages',
        investorTypes: 'qualification/investorTypes',
        milestones: 'qualification/milestones'
      }
    ),
    windowTitle () {
      return this.$t('view.SignUp.title');
    },
    individualDisabled () {
      return this.investorTypes && !this.investorTypes.includes('individual');
    },
    institutionDisabled () {
      return this.investorTypes && !this.investorTypes.includes('institution');
    }
  },
  methods: {
    ...mapActions({
      fetchInvestorTypes: 'qualification/fetchInvestorTypes',
      setSignUpInfo: 'qualification/setSignUpInfo',
      setUserEmail: 'qualification/setUserEmail'
    }),
    async submitSignUp () {
      eventBus.$emit('isSubmitting', true);
      this.isLoading = true;

      try {
        await this.signUp({
          username: this.email,
          password: this.password
        });
        this.setUserEmail(this.email);
        this.setSignUpInfo({ investorType: this.investorType, country: this.country, email: this.email });
        LoggerService.success(this.$t('view.SignUp.successMessage'));
        this.$router.push({ name: 'confirm-sign-up' });
      } catch (error) {
        if (
          error.code === 'InvalidParameterException' ||
          error.code === 'InvalidPasswordException'
        ) {
          LoggerService.error(this.$t('view.SignUp.failureMessage'));
        } else {
          LoggerService.error(error);
        }
      }
      this.isLoading = false;
      eventBus.$emit('isSubmitting', false);
    }
  },
  async mounted () {
    this.isLoading = true;
    try {
      await this.fetchInvestorTypes();
      if (this.individualDisabled) this.investorType = 'institution';
      if (this.institutionDisabled) this.investorType = 'individual';
    } catch (error) {
      LoggerService.error(error);
    }
    this.isLoading = false;
    if (process.env.VUE_APP_AUTH_REDIRECTION_URL) {
      window.location.replace(process.env.VUE_APP_AUTH_REDIRECTION_URL);
    }
  }
};
</script>

<style lang="less" scoped>
  .investor-type {
    padding: var(--gw) calc(var(--gw) * 0.5) calc(var(--gw) * 0.5);
    border: 1px solid var(--grey-500);
    border-radius: var(--radius);
  }
</style>
